import { useState } from 'react';
import Modal from '../../ui/Modal/Modal';
import Input from '../../ui/Input/Input';
import Button from '../../ui/Button/Button';
import styles from './CreateCodexModal.module.css';

const TYPES = [
  {
    value: 'general',
    title: 'General',
    desc: 'Prose, world-building, notes — the standard editor.',
  },
  {
    value: 'screenplay',
    title: 'Screenplay',
    desc: 'Courier layout with scene headings, dialog, and characters.',
  },
];

// Screenplay subtypes. `scaletta` is the legacy card-per-scene workflow;
// `plain` is a single fluid chapter with inline scene headings.
const SCREENPLAY_SUBTYPES = [
  {
    value: 'scaletta',
    title: 'Scaletta',
    desc: 'One chapter per scene. Build your screenplay from scene cards.',
  },
  {
    value: 'plain',
    title: 'Plain',
    desc: 'Single fluid chapter. Headings live inline — best for stream-of-thought.',
  },
];

// Two-step wizard:
//   1. name + codex type
//   2. screenplay subtype (only when type === 'screenplay')
//
// Step 2 is skipped for non-screenplay codices, so "Next" doubles as "Create"
// when the picker resolves to a single step. The Modal's `onBack` slot drives
// step navigation without a custom header.
const STEP_DETAILS = 'details';
const STEP_SUBTYPE = 'subtype';

export default function CreateCodexModal({ isOpen, onClose, onCreate }) {
  const [step, setStep] = useState(STEP_DETAILS);
  const [name, setName] = useState('');
  const [type, setType] = useState('general');
  const [subtype, setSubtype] = useState('scaletta');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setStep(STEP_DETAILS);
    setName('');
    setType('general');
    setSubtype('scaletta');
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const submit = async (chosenType, chosenSubtype) => {
    setSubmitting(true);
    setError(null);
    try {
      await onCreate(
        name.trim(),
        chosenType,
        chosenType === 'screenplay' ? chosenSubtype : undefined,
      );
      reset();
      onClose();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const handlePrimary = (e) => {
    e?.preventDefault?.();
    if (step === STEP_DETAILS) {
      if (!name.trim()) {
        setError('Name is required');
        return;
      }
      if (type === 'screenplay') {
        setError(null);
        setStep(STEP_SUBTYPE);
        return;
      }
      submit(type);
      return;
    }
    submit(type, subtype);
  };

  const handleBack = () => {
    if (submitting) return;
    setError(null);
    setStep(STEP_DETAILS);
  };

  const isSubtypeStep = step === STEP_SUBTYPE;
  const primaryLabel = isSubtypeStep
    ? 'Create'
    : type === 'screenplay'
      ? 'Next'
      : 'Create';
  const title = isSubtypeStep ? 'Screenplay subtype' : 'Create codex';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onBack={isSubtypeStep ? handleBack : undefined}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handlePrimary} loading={submitting}>
            {primaryLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={handlePrimary}>
        {!isSubtypeStep && (
          <>
            <Input
              label="Codex name"
              placeholder="e.g. The First Chronicle"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={error}
              autoFocus
            />
            <div className={styles.typePicker}>
              {TYPES.map((t) => (
                <button
                  type="button"
                  key={t.value}
                  className={`${styles.typeCard} ${
                    type === t.value ? styles.typeCardActive : ''
                  }`}
                  onClick={() => setType(t.value)}
                  disabled={submitting}
                >
                  <span className={styles.typeCardTitle}>{t.title}</span>
                  <span className={styles.typeCardDesc}>{t.desc}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {isSubtypeStep && (
          <>
            <p className={styles.stepHint}>
              Pick how scenes are organized in <strong>{name.trim()}</strong>.
            </p>
            <div className={styles.typePicker}>
              {SCREENPLAY_SUBTYPES.map((s) => (
                <button
                  type="button"
                  key={s.value}
                  className={`${styles.typeCard} ${
                    subtype === s.value ? styles.typeCardActive : ''
                  }`}
                  onClick={() => setSubtype(s.value)}
                  disabled={submitting}
                >
                  <span className={styles.typeCardTitle}>{s.title}</span>
                  <span className={styles.typeCardDesc}>{s.desc}</span>
                </button>
              ))}
            </div>
            {error && <p className={styles.stepError}>{error}</p>}
          </>
        )}
        {/* Submit-on-Enter without an explicit submit button. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}


